import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Buildings, FloppyDisk } from "@phosphor-icons/react";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingLH, setLoadingLH] = useState(false);

   
  useEffect(() => { api.get("/settings/company").then((r) => setForm(r.data)); }, []);

  if (!form) return <div className="p-8 text-slate-500">Loading…</div>;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/company", form);
      toast.success("Company profile saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const loadCelsius = async () => {
    if (!window.confirm("Replace the current company profile with the official Celsius letterhead (logo, signature, GSTIN, Regd. & Branch office)?")) return;
    setLoadingLH(true);
    try {
      const r = await api.post("/settings/company/load-celsius");
      setForm(r.data);
      toast.success("Celsius letterhead loaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoadingLH(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Company Settings"
        subtitle="Used across invoices, PDFs and headers."
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={loadCelsius} disabled={loadingLH} data-testid="load-celsius-letterhead-button" variant="outline" className="rounded-sm">
              {loadingLH ? "Loading…" : "Load Celsius Letterhead"}
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-company-button" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
              <FloppyDisk size={16} className="mr-2" /> {saving ? "Saving…" : "Save Profile"}
            </Button>
          </div>
        }
      />

      <div className="p-8 max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 p-6 rounded-sm lg:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <Buildings size={18} className="text-[#EA580C]" />
              <div className="label-caps">Identity</div>
            </div>
            {form.logo_url ? (
              <img src={form.logo_url} alt="logo" className="w-full max-h-32 object-contain bg-slate-50 border border-slate-200 mb-4" />
            ) : (
              <div className="w-full h-32 bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-xs text-slate-400 mb-4">
                Logo preview
              </div>
            )}
            <div className="space-y-3">
              <Field label="Company name"><Input data-testid="company-name-input" value={form.name} onChange={(e) => update("name", e.target.value)} /></Field>
              <Field label="Tagline"><Input value={form.tagline} onChange={(e) => update("tagline", e.target.value)} /></Field>
              <Field label="Logo URL"><Input value={form.logo_url} onChange={(e) => update("logo_url", e.target.value)} placeholder="https://..." /></Field>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-sm lg:col-span-2 space-y-5">
            <div>
              <div className="label-caps mb-3">Tax & Legal (India)</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="GSTIN"><Input data-testid="company-gstin-input" value={form.gstin} onChange={(e) => update("gstin", e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" /></Field>
                <Field label="PAN"><Input value={form.pan} onChange={(e) => update("pan", e.target.value.toUpperCase())} placeholder="AAAAA0000A" /></Field>
              </div>
            </div>

            <div>
              <div className="label-caps mb-3">Address</div>
              <Field label="Street address (Regd. Office)"><Textarea data-testid="company-regd-address-input" rows={2} value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Registered office — e.g. House No. 342, Ward No. 4, Mohalla Gulguliya, Badarpur" /></Field>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <Field label="City"><Input value={form.city} onChange={(e) => update("city", e.target.value)} /></Field>
                <Field label="State"><Input value={form.state} onChange={(e) => update("state", e.target.value)} /></Field>
                <Field label="Pincode"><Input value={form.pincode} onChange={(e) => update("pincode", e.target.value)} /></Field>
              </div>
              <div className="mt-3">
                <Field label="Corporate Office (shown on PO & documents)"><Textarea data-testid="company-corp-office-input" rows={2} value={form.corp_office || ""} onChange={(e) => update("corp_office", e.target.value)} placeholder="e.g. 6th Floor, No-625, Tower B3, Spaze IT Park, Sohna Road, Gurugram (Haryana) – 122018" /></Field>
              </div>
              <div className="mt-3">
                <Field label="Branch Office (shown in PO footer)"><Textarea data-testid="company-branch-office-input" rows={2} value={form.branch_office || ""} onChange={(e) => update("branch_office", e.target.value)} placeholder="e.g. Plus Offices, Cyber Park, Sector 67, Gurgaon – 122018 (HR)" /></Field>
              </div>
            </div>

            <div>
              <div className="label-caps mb-3">Contact</div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Phone"><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} /></Field>
                <Field label="Email"><Input value={form.email} onChange={(e) => update("email", e.target.value)} /></Field>
                <Field label="Website"><Input value={form.website} onChange={(e) => update("website", e.target.value)} /></Field>
              </div>
            </div>

            <div>
              <div className="label-caps mb-3">Bank Details (shown on invoices)</div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Bank name"><Input value={form.bank_name} onChange={(e) => update("bank_name", e.target.value)} /></Field>
                <Field label="Account No."><Input value={form.bank_account} onChange={(e) => update("bank_account", e.target.value)} /></Field>
                <Field label="IFSC"><Input value={form.bank_ifsc} onChange={(e) => update("bank_ifsc", e.target.value.toUpperCase())} /></Field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
